// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Marker metrics and label placement for the fleet map.
//
// Everything the map draws on top of the artwork — dots, halos, names, site
// keys — is sized in SCREEN PIXELS and converted to geometry units at the
// current zoom (see `unitsPerPx` in FleetMap). Sizing them in geometry units
// instead makes a dot the size of a state on a tight fleet; sizing them
// against the artwork's own width (the previous rule) makes them correct
// only at one frame width, and the fleet map's frame is far wider than the
// artwork is, so every marker rendered about twice its intended size.
//
// Placement then has two jobs the previous fixed "above the dot" rule did
// not do, both of which showed up on the demo scenarios:
//
//  1. A label near the top of the frame was drawn off it — the marker was
//     inside the viewBox, its name was not (/fleet/demo/3). Labels flip
//     below the dot when there is no room above, and are pulled inside the
//     frame horizontally.
//  2. A fleet of five Southeast sites, or a nationwide one, drew names on
//     top of each other, and on top of neighbouring dots (/fleet/demo/1,
//     /fleet/demo/4). A label that would collide with another label or with
//     any marker moves to the other side of its own marker; if that side is
//     taken too it is dropped, highest-priority marker first. A dropped name is not
//     lost: hovering the marker names the site in the hover card, and every
//     site is listed in the cards below the map.

import type { Point } from './projection';
import type { ViewBox } from './autoFit';

/**
 * The map's size table, in CSS pixels at any zoom.
 *
 * Tuned against the demo scenarios: the previous values rendered at roughly
 * twice these on a full-width frame, which read as cluttered as soon as a
 * fleet had more than a couple of sites.
 */
export const MAP_PX = {
  /** The solid dot. */
  coreR: 5,
  /** The soft ring around it. Also the clustering distance (× 2). */
  haloR: 11,
  /** Site name. */
  label: 12,
  /** The mono `site_key` line under the name. */
  key: 9.5,
  /** The member count inside a cluster's dot. */
  count: 8,
  /** Gap between the halo and the nearest edge of the label block. */
  labelGap: 5,
  /** Baseline-to-baseline, name → key line. */
  lineHeight: 12,
} as const;

/** Rough glyph width as a fraction of font size, for collision boxes. */
const GLYPH_WIDTH_RATIO = 0.6;

/** How close a label may come to the frame edge. */
const EDGE_PAD_PX = 4;

export interface LabelInput {
  /** Cluster id. */
  id: string;
  point: Point;
  /** Site name, or "N data centers" for a cluster. */
  title: string;
  /** The `site_key`, or the place for a cluster. */
  subtitle: string;
  /**
   * Higher wins when two labels collide — see `labelPriority` in FleetMap for
   * what goes into it (cluster size, then posture).
   */
  priority: number;
}

/**
 * Which end of the text sits at `x` — an SVG `text-anchor`. `middle` for a
 * label above or below its dot, `start` for one to its right, `end` for one
 * to its left.
 */
export type LabelAnchor = 'middle' | 'start' | 'end';

export interface LabelPlacement {
  /** The anchored edge of both text lines, in geometry units. */
  x: number;
  anchor: LabelAnchor;
  /** Baseline of the title line. */
  titleY: number;
  /** Baseline of the subtitle line. */
  subtitleY: number;
  /**
   * False when this label collided with a marker or with a higher-priority
   * label. The marker still renders; only its text is held back, and the
   * hover card names the site.
   */
  visible: boolean;
}

interface Box {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

interface Candidate {
  x: number;
  anchor: LabelAnchor;
  titleY: number;
  box: Box;
}

/**
 * Place every marker's label, and decide which of them fit.
 *
 * Pure: same inputs, same output — the layout is a function of the fleet and
 * the current frame, exactly like the viewBox it is computed against.
 */
export function layOutLabels(
  markers: readonly LabelInput[],
  { view, unitsPerPx }: { view: ViewBox; unitsPerPx: number }
): Map<string, LabelPlacement> {
  const u = (px: number) => px * unitsPerPx;
  const placements = new Map<string, LabelPlacement>();

  /** A label has to be wholly within the frame, or it is drawn off it. */
  const inside = (b: Box) =>
    b.minX >= view.x + u(EDGE_PAD_PX) &&
    b.maxX <= view.x + view.width - u(EDGE_PAD_PX) &&
    b.minY >= view.y + u(EDGE_PAD_PX) &&
    b.maxY <= view.y + view.height - u(EDGE_PAD_PX);

  // Every marker's own footprint is occupied space from the start, so a name
  // is never written across a neighbouring dot — which is unreadable and, on
  // a green marker, hides the one thing the marker is there to show.
  const kept: Box[] = markers.map(m => ({
    minX: m.point.x - u(MAP_PX.haloR),
    maxX: m.point.x + u(MAP_PX.haloR),
    minY: m.point.y - u(MAP_PX.haloR),
    maxY: m.point.y + u(MAP_PX.haloR),
  }));

  // Biggest cluster first, id as the tie-break so the result does not depend
  // on the order sites happened to arrive in.
  const ordered = [...markers].sort((a, b) => b.priority - a.priority || a.id.localeCompare(b.id));

  for (const marker of ordered) {
    const width =
      u(Math.max(marker.title.length * MAP_PX.label, marker.subtitle.length * MAP_PX.key)) *
      GLYPH_WIDTH_RATIO;

    // Above the dot first — that is where the mock puts it — then below, then
    // beside it. Exhausting all four sides before giving up matters on a
    // crowded fleet: in the dense middle of the dataset most names are
    // placeable on exactly one side, and dropping them instead loses
    // information the map has room to show.
    const candidates = candidatePlacements(marker, width, view, u);
    const fitted = candidates.find(
      candidate => inside(candidate.box) && !kept.some(other => overlaps(other, candidate.box))
    );

    // Nothing fits: keep the default position so the placement is still well
    // defined, but do not paint the text.
    const chosen = fitted ?? candidates[0];
    if (fitted) kept.push(fitted.box);

    placements.set(marker.id, {
      x: chosen.x,
      anchor: chosen.anchor,
      titleY: chosen.titleY,
      subtitleY: chosen.titleY + u(MAP_PX.lineHeight),
      visible: Boolean(fitted),
    });
  }

  return placements;
}

/**
 * The four places a label may go, in the order they are tried: above the dot,
 * below it, to its right, to its left.
 */
function candidatePlacements(
  marker: LabelInput,
  width: number,
  view: ViewBox,
  u: (_px: number) => number
): Candidate[] {
  const offset = u(MAP_PX.haloR + MAP_PX.labelGap);

  /** The space two lines of text occupy, from their left edge and baseline. */
  const box = (minX: number, titleY: number): Box => ({
    minX,
    maxX: minX + width,
    minY: titleY - u(MAP_PX.label),
    // The subtitle sits a line below the title, plus a little for descenders.
    maxY: titleY + u(MAP_PX.lineHeight + MAP_PX.key * 0.25),
  });

  /** Centred above (`dir` -1) or below (+1) the dot. */
  const stacked = (dir: -1 | 1): Candidate => {
    // Pull the block inside the frame horizontally. Clamping with lo > hi
    // would flip the label out the other side, so a label wider than the
    // frame stays centred on its marker instead.
    const loX = view.x + width / 2 + u(EDGE_PAD_PX);
    const hiX = view.x + view.width - width / 2 - u(EDGE_PAD_PX);
    const x = loX <= hiX ? Math.max(loX, Math.min(hiX, marker.point.x)) : marker.point.x;
    const titleY =
      dir === -1
        ? marker.point.y - offset - u(MAP_PX.lineHeight)
        : marker.point.y + offset + u(MAP_PX.label);
    return { x, anchor: 'middle', titleY, box: box(x - width / 2, titleY) };
  };

  /** Vertically centred to the right (`dir` +1) or left (-1) of the dot. */
  const beside = (dir: -1 | 1): Candidate => {
    const x = marker.point.x + dir * offset;
    // Centred on the dot, so a side label reads as belonging to it rather
    // than to whatever sits above or below it.
    const titleY = marker.point.y - u(MAP_PX.lineHeight - MAP_PX.label) / 2;
    return {
      x,
      anchor: dir === 1 ? 'start' : 'end',
      titleY,
      box: box(dir === 1 ? x : x - width, titleY),
    };
  };

  return [stacked(-1), stacked(1), beside(1), beside(-1)];
}

function overlaps(a: Box, b: Box): boolean {
  return a.minX < b.maxX && b.minX < a.maxX && a.minY < b.maxY && b.minY < a.maxY;
}
